import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Heart, Minus, Plus, ChevronLeft, ChevronRight, ChevronDown, Info } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { useEvent } from '../context/EventContext';
import { store } from '../lib/localStore';
import { api } from '../lib/api';
import { pick } from '../lib/i18nContent';
import { ImagePlaceholder } from '../components/ImagePlaceholder';
import { StarRating } from '../components/StarRating';

export default function Taste() {
  const { t, lang } = useLang();
  const { config } = useEvent();
  const navigate = useNavigate();
  const products = config?.products || [];
  const aromaTags = config?.aroma_tags || [];
  const eventId = config?.event?.id;
  const votingEnabled = config?.settings?.voting_enabled;

  const [active, setActive] = useState(0);
  const [tasting, setTasting] = useState({});
  const [favourite, setFavourite] = useState(null);
  const [showMore, setShowMore] = useState(false);

  useEffect(() => setShowMore(false), [active]);

  useEffect(() => {
    if (!eventId) return;
    setTasting(store.getTasting(eventId));
    setFavourite(store.getFavourite(eventId));
  }, [eventId]);

  const product = products[active];
  const data = (product && tasting[product.id]) || {};

  const syncToBackend = useCallback((productId, d, isFav) => {
    if (!eventId || !productId) return;
    const token = store.getToken(eventId);
    api.syncTasting({
      event_id: eventId, token, product_id: productId,
      rating: d.rating || null, aromas: d.aromas || [], favourite: !!isFav,
    }).catch(() => {});
  }, [eventId]);

  const patch = useCallback((changes) => {
    const all = store.updateProduct(eventId, product.id, changes);
    setTasting({ ...all });
    // anonymous analytics sync (no free-text notes)
    if ('rating' in changes || 'aromas' in changes) {
      syncToBackend(product.id, all[product.id], favourite === product.id);
    }
  }, [eventId, product, favourite, syncToBackend]);

  const toggleAroma = (tag) => {
    const current = data.aromas || [];
    const next = current.includes(tag) ? current.filter((a) => a !== tag) : [...current, tag];
    patch({ aromas: next });
  };

  const setQty = (q) => patch({ quantity: Math.max(0, q) });

  const chooseFavourite = async () => {
    const token = store.getToken(eventId);
    const currentlyFav = favourite === product.id;
    try {
      if (currentlyFav) {
        await api.removeVote(eventId, token);
        store.setFavourite(eventId, null);
        setFavourite(null);
        syncToBackend(product.id, data, false);
        toast.success(t('favouriteRemoved'));
      } else {
        await api.castVote({ event_id: eventId, token, product_id: product.id });
        store.setFavourite(eventId, product.id);
        setFavourite(product.id);
        syncToBackend(product.id, data, true);
        toast.success(t('voteRecorded'));
      }
    } catch (e) {
      toast.error(t('errorGeneric'));
    }
  };

  if (!product) return null;
  const isFav = favourite === product.id;

  return (
    <div data-testid="taste-screen">
      <h1 className="font-serif font-bold text-3xl text-[#FDFBF7]">{t('taste_title')}</h1>
      <p className="font-sans text-sm text-slate-400 mb-4">{t('taste_intro')}</p>

      {/* cuvée selector */}
      <div className="flex gap-2 overflow-x-auto pb-3 -mx-4 px-4 mb-2">
        {products.map((p, i) => (
          <button
            key={p.id}
            data-testid={`taste-tab-${p.order}`}
            onClick={() => setActive(i)}
            className={`shrink-0 w-9 h-9 rounded-full font-mono text-sm border transition-all ${
              i === active
                ? 'bg-[#D4AF37] text-[#070C1E] border-[#E5C158] font-semibold'
                : 'bg-[#111A38] text-slate-300 border-[#D4AF37]/25'
            }`}
          >
            {p.order}
          </button>
        ))}
      </div>

      <div className="bg-gradient-to-b from-[#111A38] to-[#0B132B] border border-[#D4AF37]/25 rounded-2xl p-5 shadow-xl fade-up" key={product.id}>
        <span className="font-mono text-[10px] tracking-widest uppercase bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#E5C158] px-3 py-1 rounded-full inline-block mb-3">
          {t('cuvee')} {product.order} / {products.length}
        </span>

        <ImagePlaceholder
          ratio="aspect-[4/5]"
          className="max-h-72 mx-auto"
          src={product.image_url}
          alt={product.name}
          contain
          credit={product.photo_credit}
          label="Bottle photo — supplied later"
        />

        <h2 className="font-serif font-semibold text-2xl text-[#FDFBF7] mt-4">{product.name}</h2>
        <p className="font-mono text-[11px] tracking-wide uppercase text-[#E5C158]/80 mt-1">{product.kind}</p>

        <div className="mt-3 bg-[#070C1E]/60 border border-[#D4AF37]/15 rounded-lg px-3 py-2">
          <span className="font-mono text-[10px] tracking-widest uppercase text-slate-500">{t('blend')}</span>
          <p className="font-sans text-sm text-slate-200 mt-0.5">{product.blend}</p>
        </div>

        {(product.technical_data || product.house_notes_i18n || product.official_aromas_i18n) && (() => {
          const td = product.technical_data || {};
          const houseNotes = pick(product.house_notes_i18n, lang);
          const officialAromas = pick(product.official_aromas_i18n, lang) || [];
          const tdRows = [['terroir', td.terroir], ['crus', td.crus], ['dosage', td.dosage],
            ['ageing', td.ageing], ['alcohol', td.alcohol], ['reserveWine', td.reserve_wine]].filter(([, v]) => v);
          return (
            <div className="mt-3">
              <button data-testid="more-about-cuvee" onClick={() => setShowMore((s) => !s)}
                className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg bg-[#18244D] border border-[#D4AF37]/25 text-[#E5C158] text-[11px] font-mono tracking-widest uppercase">
                {t('moreAboutCuvee')} <ChevronDown size={16} className={`transition-transform ${showMore ? 'rotate-180' : ''}`} />
              </button>
              {showMore && (
                <div className="mt-2 bg-[#070C1E]/60 border border-[#D4AF37]/15 rounded-lg p-3 space-y-3 fade-up" data-testid="cuvee-details">
                  {tdRows.length > 0 && (
                    <div>
                      <span className="font-mono text-[9px] tracking-widest uppercase text-[#D4AF37]/70">{t('officialHouseNotes')}</span>
                      <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-1.5">
                        {tdRows.map(([k, v]) => (
                          <div key={k}>
                            <p className="text-[9px] font-mono uppercase tracking-wide text-slate-500">{t(k)}</p>
                            <p className="text-[13px] text-slate-200">{v}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {houseNotes && <p className="text-[13px] text-slate-300 italic leading-snug">{houseNotes}</p>}
                  {officialAromas.length > 0 && (
                    <div>
                      <span className="font-mono text-[9px] tracking-widest uppercase text-[#D4AF37]/70">{t('houseTastingNotes')}</span>
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {officialAromas.map((a, i) => (
                          <span key={i} className="text-[11px] px-2.5 py-1 rounded-full bg-[#111A38] border border-[#D4AF37]/25 text-slate-200">{a}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* rating */}
        <div className="mt-5">
          <label className="font-mono text-[10px] tracking-widest uppercase text-slate-400">{t('yourRating')}</label>
          <div className="mt-2">
            <StarRating value={data.rating || 0} onChange={(v) => patch({ rating: v })} testIdPrefix={`tasting-card-rating`} />
          </div>
        </div>

        {/* aromas */}
        <div className="mt-5">
          <label className="font-mono text-[10px] tracking-widest uppercase text-slate-400">{t('aromas')}</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {aromaTags.map((tag) => {
              const selected = (data.aromas || []).includes(tag);
              return (
                <button
                  key={tag}
                  data-testid={`aroma-chip-${tag}`}
                  onClick={() => toggleAroma(tag)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                    selected
                      ? 'bg-[#D4AF37] text-[#070C1E] font-semibold border-[#E5C158]'
                      : 'bg-[#18244D] text-slate-200 border-[#D4AF37]/25'
                  }`}
                >
                  {t(`aroma_${tag}`)}
                </button>
              );
            })}
          </div>
        </div>

        {/* notes */}
        <div className="mt-5">
          <label className="font-mono text-[10px] tracking-widest uppercase text-slate-400">{t('myImpressions')}</label>
          <textarea
            data-testid="tasting-notes-input"
            value={data.notes || ''}
            onChange={(e) => patch({ notes: e.target.value })}
            placeholder={t('notesPlaceholder')}
            rows={3}
            className="w-full mt-2 bg-[#070C1E] border border-[#D4AF37]/25 rounded-lg p-3 text-sm text-slate-100 placeholder:text-slate-600 focus:border-[#D4AF37] outline-none resize-none"
          />
          <p className="flex items-start gap-1.5 mt-2 text-[10px] text-slate-500 leading-snug">
            <Info size={12} className="mt-0.5 shrink-0" /> {t('privacyNote')}
          </p>
        </div>

        {/* favourite */}
        {votingEnabled && (
          <button
            data-testid="favourite-button"
            onClick={chooseFavourite}
            className={`w-full mt-5 flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold transition-all border ${
              isFav
                ? 'bg-[#E63946]/15 text-[#E63946] border-[#E63946]/50'
                : 'bg-transparent text-[#E5C158] border-[#D4AF37]/40 hover:bg-[#D4AF37]/10'
            }`}
          >
            <Heart size={18} fill={isFav ? '#E63946' : 'none'} />
            {isFav ? t('favourited') : t('favourite')}
          </button>
        )}

        {/* quantity */}
        <div className="mt-5 flex items-center justify-between">
          <span className="font-mono text-[10px] tracking-widest uppercase text-slate-400">{t('bottlesToRequest')}</span>
          <div className="flex items-center bg-[#070C1E] border border-[#D4AF37]/30 rounded-lg">
            <button data-testid="qty-decrease" onClick={() => setQty((data.quantity || 0) - 1)} className="p-2.5 text-[#E5C158]"><Minus size={16} /></button>
            <span data-testid="qty-value" className="w-8 text-center font-mono text-slate-100">{data.quantity || 0}</span>
            <button data-testid="qty-increase" onClick={() => setQty((data.quantity || 0) + 1)} className="p-2.5 text-[#E5C158]"><Plus size={16} /></button>
          </div>
        </div>
      </div>

      {/* prev / next */}
      <div className="flex justify-between items-center mt-5">
        <button
          data-testid="taste-prev"
          disabled={active === 0}
          onClick={() => setActive((i) => Math.max(0, i - 1))}
          className="flex items-center gap-1 text-sm text-slate-300 disabled:opacity-30 px-3 py-2"
        >
          <ChevronLeft size={18} /> {t('prev')}
        </button>
        {active < products.length - 1 ? (
          <button
            data-testid="taste-next"
            onClick={() => setActive((i) => Math.min(products.length - 1, i + 1))}
            className="flex items-center gap-1 text-sm text-slate-300 px-3 py-2"
          >
            {t('next')} <ChevronRight size={18} />
          </button>
        ) : (
          <button
            data-testid="taste-finish"
            onClick={() => navigate('/notes')}
            className="flex items-center gap-1.5 text-sm font-bold text-[#070C1E] bg-gradient-to-r from-[#D4AF37] to-[#E5C158] px-5 py-2.5 rounded-lg active:scale-[0.98] transition-all"
          >
            {t('finishTasting')} <ChevronRight size={18} />
          </button>
        )}
      </div>
    </div>
  );
}
